/**
 * LLM-расходы: totals + split «чат vs compare» + byKind/byUser/byModel/byOwner + последние строки.
 */

import {
    Alert, Box, Button, Card, Center, Group, Loader, SimpleGrid, Stack, Table,
    Text, TextInput, Title,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { fetchAdminUsage, type UsageGroup, type UsageResponse, type UsageRow, type UsageTotals } from '../../lib/api-admin';
import { DESKTOP_QUERY } from '../../lib/breakpoints';
import { fmtNum, fmtUsd, shortModel } from './format';

export function AdminUsagePage(): JSX.Element {
    const [userFilter,  setUserFilter]  = useState('');
    const [ownerFilter, setOwnerFilter] = useState('');
    const [from,        setFrom]        = useState('');
    const [to,          setTo]          = useState('');

    const q = useQuery({
        queryKey: ['admin-usage', userFilter, ownerFilter, from, to],
        queryFn:  () => fetchAdminUsage({
            user:  userFilter  || undefined,
            owner: ownerFilter || undefined,
            from:  from || undefined,
            to:    to   || undefined,
            limit: 500,
        }),
        retry: false,
    });

    return (
        <Stack gap="md">
            <Title order={2}>Токены</Title>
            <Card withBorder padding="md">
                <Group gap="sm" wrap="wrap" align="end">
                    <TextInput label="User"      value={userFilter}  onChange={e => setUserFilter(e.currentTarget.value)}  placeholder="email…" flex={1} miw={180} />
                    <TextInput label="Owner/repo" value={ownerFilter} onChange={e => setOwnerFilter(e.currentTarget.value)} placeholder="acme/widget" flex={1} miw={180} />
                    <TextInput label="From (ISO)" value={from}       onChange={e => setFrom(e.currentTarget.value)} placeholder="2026-01-01" w={140} />
                    <TextInput label="To (ISO)"   value={to}         onChange={e => setTo(e.currentTarget.value)}   placeholder="2026-12-31" w={140} />
                    <Button variant="subtle" onClick={() => { setUserFilter(''); setOwnerFilter(''); setFrom(''); setTo(''); }}>
                        Сброс
                    </Button>
                </Group>
            </Card>

            {q.isLoading && <Center my="lg"><Loader /></Center>}
            {q.error && <Alert color="red">{(q.error as Error).message}</Alert>}
            {q.data && <UsageView data={q.data} />}
        </Stack>
    );
}

function UsageView({ data }: { data: UsageResponse }): JSX.Element {
    return (
        <Stack gap="lg">
            <TotalsBar totals={data.totals} />
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <SplitCard title="Чат-ассистент" totals={data.split.chat} />
                <SplitCard title="Compare (aspect + rollup + entity)" totals={data.split.compare} />
            </SimpleGrid>
            <SimpleGrid cols={{ base: 1, md: 2, lg: 4 }} spacing="md">
                <GroupCard title="По типу"            rows={data.byKind} />
                <GroupCard title="По пользователям"   rows={data.byUser} />
                <GroupCard title="По моделям"         rows={data.byModel} keyFmt={shortModel} />
                <GroupCard title="По репозиториям"    rows={data.byOwner} />
            </SimpleGrid>
            <Box>
                <Title order={4} mb="xs">Последние вызовы ({data.rows.length})</Title>
                <RowsTable rows={data.rows} />
            </Box>
        </Stack>
    );
}

function TotalsBar({ totals }: { totals: UsageTotals }): JSX.Element {
    return (
        <Card withBorder padding="md">
            <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="md">
                <Stat label="Вызовов" value={fmtNum(totals.calls)} />
                <Stat label="Расход"  value={fmtUsd(totals.costUsd)} />
                <Stat label="Input"   value={fmtNum(totals.inputTokens)} />
                <Stat label="Output"  value={fmtNum(totals.outputTokens)} />
                <Stat label="Cache R" value={fmtNum(totals.cacheReadTokens)} />
                <Stat label="Cache W" value={fmtNum(totals.cacheWriteTokens)} />
            </SimpleGrid>
        </Card>
    );
}

function SplitCard({ title, totals }: { title: string; totals: UsageTotals }): JSX.Element {
    return (
        <Card withBorder padding="md">
            <Text fw={600} mb="xs">{title}</Text>
            <Group justify="space-between"><Text fz="sm" c="dimmed">Вызовов</Text><Text fz="sm">{fmtNum(totals.calls)}</Text></Group>
            <Group justify="space-between"><Text fz="sm" c="dimmed">Расход</Text><Text fz="sm" fw={600}>{fmtUsd(totals.costUsd)}</Text></Group>
            <Group justify="space-between"><Text fz="sm" c="dimmed">Input</Text><Text fz="sm">{fmtNum(totals.inputTokens)}</Text></Group>
            <Group justify="space-between"><Text fz="sm" c="dimmed">Output</Text><Text fz="sm">{fmtNum(totals.outputTokens)}</Text></Group>
        </Card>
    );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
    return (
        <Box>
            <Text fz="xs" c="dimmed" tt="uppercase">{label}</Text>
            <Text fz="md" fw={600}>{value}</Text>
        </Box>
    );
}

function GroupCard({ title, rows, keyFmt }: { title: string; rows: UsageGroup[]; keyFmt?: (k: string) => string }): JSX.Element {
    return (
        <Card withBorder padding="sm">
            <Text fw={600} mb="xs">{title}</Text>
            {rows.length === 0
                ? <Text c="dimmed" fz="sm">нет данных</Text>
                : (
                    <Table fz="sm" verticalSpacing={4}>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Ключ</Table.Th>
                                <Table.Th>Вызовов</Table.Th>
                                <Table.Th>$</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {rows.slice(0, 10).map(r => (
                                <Table.Tr key={r.key}>
                                    <Table.Td style={{ maxWidth: 200 }}>
                                        <Text fz="sm" truncate>{keyFmt ? keyFmt(r.key) : r.key}</Text>
                                    </Table.Td>
                                    <Table.Td>{fmtNum(r.totals.calls)}</Table.Td>
                                    <Table.Td>{fmtUsd(r.totals.costUsd)}</Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                )
            }
        </Card>
    );
}

function RowsTable({ rows }: { rows: UsageRow[] }): JSX.Element {
    const isDesktop = useMediaQuery(DESKTOP_QUERY);
    if (rows.length === 0) return <Text c="dimmed" fz="sm">Нет вызовов за выбранный период.</Text>;

    if (!isDesktop) {
        return (
            <Stack gap="xs">
                {rows.slice(0, 50).map((r, i) => (
                    <Card withBorder padding="xs" key={i}>
                        <Group justify="space-between" wrap="nowrap">
                            <Text fz="xs" ff="monospace" truncate>{r.tsServer.replace('T', ' ').slice(0, 19)}</Text>
                            <Text fz="xs">{shortModel(r.model)} · {r.kind}</Text>
                        </Group>
                        <Text fz="xs" c="dimmed" truncate>{r.user} · {r.owner}/{r.repo}</Text>
                        <Group gap="xs" fz="xs" c="dimmed">
                            <Text fz="xs">in {fmtNum(r.inputTokens + r.cacheReadTokens + r.cacheWriteTokens)}</Text>
                            <Text fz="xs">out {fmtNum(r.outputTokens)}</Text>
                            <Text fz="xs">{fmtUsd(r.costUsd)}</Text>
                            <Text fz="xs">{r.latencyMs}ms</Text>
                        </Group>
                    </Card>
                ))}
            </Stack>
        );
    }

    return (
        <Card withBorder padding={0}>
            <Table fz="sm" striped highlightOnHover verticalSpacing="xs">
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Время</Table.Th>
                        <Table.Th>User</Table.Th>
                        <Table.Th>Repo</Table.Th>
                        <Table.Th>Model</Table.Th>
                        <Table.Th>Kind</Table.Th>
                        <Table.Th>In</Table.Th>
                        <Table.Th>Out</Table.Th>
                        <Table.Th>$</Table.Th>
                        <Table.Th>ms</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.map((r, i) => (
                        <Table.Tr key={i}>
                            <Table.Td><Text fz="xs" ff="monospace">{r.tsServer.replace('T', ' ').slice(0, 19)}</Text></Table.Td>
                            <Table.Td><Text fz="xs">{r.user}</Text></Table.Td>
                            <Table.Td><Text fz="xs">{r.owner}/{r.repo}</Text></Table.Td>
                            <Table.Td><Text fz="xs">{shortModel(r.model)}</Text></Table.Td>
                            <Table.Td>{r.kind}</Table.Td>
                            <Table.Td>{fmtNum(r.inputTokens + r.cacheReadTokens + r.cacheWriteTokens)}</Table.Td>
                            <Table.Td>{fmtNum(r.outputTokens)}</Table.Td>
                            <Table.Td>{fmtUsd(r.costUsd)}</Table.Td>
                            <Table.Td>{r.latencyMs}</Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </Card>
    );
}
